<template>
  <div class="c-animation-selector w-full">
    <NSelect :disabled="isAnimating" :options="animationOptions" :value="modelValue" @update:value="applyAnimation" />
    <div ref="animatedElement" class="mt-3 w-full h-12 bg-primary" />
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount } from 'vue'
import { NSelect } from 'naive-ui'
import { useI18n } from 'vue-i18n'

withDefaults(
  defineProps<{
    modelValue?: string
  }>(),
  {
    modelValue: 'none',
  },
)

const emit = defineEmits<{
  'update:modelValue': [a: string]
}>()
const isAnimating = ref(false)
const { t } = useI18n()
const animatedElement = ref<HTMLElement | null>(null)
const OPTIONS = [
  { value: 'none', label: t('canvasUI.animations.none') },
  {
    value: 'bounce',
    label: t('canvasUI.animations.bounce'),
  },
  {
    value: 'flash',
    label: t('canvasUI.animations.flash'),
  },
  {
    value: 'pulse',
    label: t('canvasUI.animations.pulse'),
  },
  {
    value: 'rubberBand',
    label: t('canvasUI.animations.rubberBand'),
  },
  {
    value: 'shakeX',
    label: t('canvasUI.animations.shakeX'),
  },
  {
    value: 'shakeY',
    label: t('canvasUI.animations.shakeY'),
  },
  {
    value: 'headShake',
    label: t('canvasUI.animations.headShake'),
  },
  {
    value: 'swing',
    label: t('canvasUI.animations.swing'),
  },
  {
    value: 'tada',
    label: t('canvasUI.animations.tada'),
  },
  {
    value: 'wobble',
    label: t('canvasUI.animations.wobble'),
  },
  {
    value: 'jello',
    label: t('canvasUI.animations.jello'),
  },
  {
    value: 'heartBeat',
    label: t('canvasUI.animations.heartBeat'),
  },
  {
    value: 'backInDown',
    label: t('canvasUI.animations.entrances.backInDown'),
  },
  {
    value: 'backInLeft',
    label: t('canvasUI.animations.entrances.backInLeft'),
  },
  {
    value: 'backInUp',
    label: t('canvasUI.animations.entrances.backInUp'),
  },
  {
    value: 'bounceIn',
    label: t('canvasUI.animations.entrances.bounceIn'),
  },
  {
    value: 'bounceInDown',
    label: t('canvasUI.animations.entrances.bounceInDown'),
  },
  {
    value: 'bounceInLeft',
    label: t('canvasUI.animations.entrances.bounceInLeft'),
  },
  {
    value: 'bounceInUp',
    label: t('canvasUI.animations.entrances.bounceInUp'),
  },
  {
    value: 'fadeIn',
    label: t('canvasUI.animations.entrances.fadeIn'),
  },
  {
    value: 'fadeInDown',
    label: t('canvasUI.animations.entrances.fadeInDown'),
  },
  {
    value: 'fadeInDownBig',
    label: t('canvasUI.animations.entrances.fadeInDownBig'),
  },
  {
    value: 'fadeInLeft',
    label: t('canvasUI.animations.entrances.fadeInLeft'),
  },
  {
    value: 'fadeInLeftBig',
    label: t('canvasUI.animations.entrances.fadeInLeftBig'),
  },
  {
    value: 'fadeInUp',
    label: t('canvasUI.animations.entrances.fadeInUp'),
  },
  {
    value: 'fadeInUpBig',
    label: t('canvasUI.animations.entrances.fadeInUpBig'),
  },
  {
    value: 'flipInX',
    label: t('canvasUI.animations.entrances.flipInX'),
  },
  {
    value: 'flipInY',
    label: t('canvasUI.animations.entrances.flipInY'),
  },
  {
    value: 'lightSpeedInLeft',
    label: t('canvasUI.animations.entrances.lightSpeedInLeft'),
  },
  {
    value: 'rotateIn',
    label: t('canvasUI.animations.entrances.rotateIn'),
  },
  {
    value: 'rotateInDownLeft',
    label: t('canvasUI.animations.entrances.rotateInDownLeft'),
  },
  {
    value: 'rotateInUpLeft',
    label: t('canvasUI.animations.entrances.rotateInUpLeft'),
  },
  {
    value: 'jackInTheBox',
    label: t('canvasUI.animations.entrances.jackInTheBox'),
  },
  {
    value: 'rollIn',
    label: t('canvasUI.animations.entrances.rollIn'),
  },
  {
    value: 'zoomIn',
    label: t('canvasUI.animations.entrances.zoomIn'),
  },
  {
    value: 'zoomInDown',
    label: t('canvasUI.animations.entrances.zoomInDown'),
  },
  {
    value: 'zoomInLeft',
    label: t('canvasUI.animations.entrances.zoomInLeft'),
  },
  {
    value: 'zoomInUp',
    label: t('canvasUI.animations.entrances.zoomInUp'),
  },
  {
    value: 'slideInDown',
    label: t('canvasUI.animations.entrances.slideInDown'),
  },
  {
    value: 'slideInLeft',
    label: t('canvasUI.animations.entrances.slideInLeft'),
  },
  {
    value: 'slideInUp',
    label: t('canvasUI.animations.entrances.slideInUp'),
  },
] as const

const animationOptions = [...OPTIONS]

let timeoutId: ReturnType<typeof setTimeout> | null = null
const applyAnimation = (animation: (typeof OPTIONS)[number]['value']) => {
  emit('update:modelValue', animation)
  if (!animatedElement.value || animation === 'none') return

  animatedElement.value.classList.add('animate__animated', `animate__${animation}`)
  isAnimating.value = true
  timeoutId = setTimeout(() => {
    animatedElement.value?.classList.remove('animate__animated', `animate__${animation}`)
    isAnimating.value = false
  }, 1000)
}

onBeforeUnmount(() => {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
})
</script>
